import { useCallback, useEffect, useState } from 'react';
import { supabase } from '../lib/supabase';
import type { Quadrant, Task } from '../types';

interface UseTasksResult {
  tasks: Task[];
  loading: boolean;
  error: string | null;
  addTask: (quadrant: Quadrant, title: string) => Promise<void>;
  updateTask: (id: string, patch: Partial<Pick<Task, 'title' | 'notes' | 'quadrant' | 'position'>>) => Promise<void>;
  deleteTask: (id: string) => Promise<void>;
  moveTask: (id: string, toQuadrant: Quadrant) => Promise<void>;
  importTasks: (rows: Array<{ quadrant: Quadrant; title: string; position: number }>) => Promise<void>;
}

export function useTasks(enabled: boolean): UseTasksResult {
  const [tasks, setTasks] = useState<Task[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Initial fetch
  useEffect(() => {
    if (!enabled) {
      setLoading(false);
      return;
    }

    let cancelled = false;

    (async () => {
      setLoading(true);
      const { data, error } = await supabase
        .from('tasks')
        .select('*')
        .order('quadrant', { ascending: true })
        .order('position', { ascending: true });

      if (cancelled) return;

      if (error) {
        setError(error.message);
        setLoading(false);
        return;
      }

      setTasks((data ?? []) as Task[]);
      setError(null);
      setLoading(false);
    })();

    return () => { cancelled = true; };
  }, [enabled]);

  // Realtime subscription
  useEffect(() => {
    if (!enabled) return;

    const channel = supabase
      .channel('tasks-realtime')
      .on(
        'postgres_changes',
        { event: '*', schema: 'public', table: 'tasks' },
        (payload) => {
          setTasks((prev) => {
            if (payload.eventType === 'INSERT') {
              const row = payload.new as Task;
              if (prev.some((t) => t.id === row.id)) return prev;
              return [...prev, row].sort(sortTasks);
            }
            if (payload.eventType === 'UPDATE') {
              const row = payload.new as Task;
              return prev.map((t) => (t.id === row.id ? row : t)).sort(sortTasks);
            }
            if (payload.eventType === 'DELETE') {
              const row = payload.old as Task;
              return prev.filter((t) => t.id !== row.id);
            }
            return prev;
          });
        },
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [enabled]);

  const addTask = useCallback(async (quadrant: Quadrant, title: string) => {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) throw new Error('Not authenticated');

    const maxPos = Math.max(
      -1,
      ...tasks.filter((t) => t.quadrant === quadrant).map((t) => t.position),
    );

    const { error } = await supabase.from('tasks').insert({
      owner_id: user.id,
      quadrant,
      title,
      position: maxPos + 1,
    });
    if (error) throw error;
  }, [tasks]);

  const updateTask = useCallback(async (id: string, patch: Partial<Pick<Task, 'title' | 'notes' | 'quadrant' | 'position'>>) => {
    const { error } = await supabase.from('tasks').update(patch).eq('id', id);
    if (error) throw error;
  }, []);

  const deleteTask = useCallback(async (id: string) => {
    const { error } = await supabase.from('tasks').delete().eq('id', id);
    if (error) throw error;
  }, []);

  const moveTask = useCallback(async (id: string, toQuadrant: Quadrant) => {
    const maxPos = Math.max(
      -1,
      ...tasks.filter((t) => t.quadrant === toQuadrant && t.id !== id).map((t) => t.position),
    );
    const { error } = await supabase
      .from('tasks')
      .update({ quadrant: toQuadrant, position: maxPos + 1 })
      .eq('id', id);
    if (error) throw error;
  }, [tasks]);

  const importTasks = useCallback(async (rows: Array<{ quadrant: Quadrant; title: string; position: number }>) => {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) throw new Error('Not authenticated');

    // Replace all tasks for this owner
    const { error: delErr } = await supabase.from('tasks').delete().eq('owner_id', user.id);
    if (delErr) throw delErr;

    const payload = rows.map((r) => ({ ...r, owner_id: user.id }));
    const { error: insErr } = await supabase.from('tasks').insert(payload);
    if (insErr) throw insErr;
  }, []);

  return { tasks, loading, error, addTask, updateTask, deleteTask, moveTask, importTasks };
}

function sortTasks(a: Task, b: Task): number {
  if (a.quadrant !== b.quadrant) return a.quadrant.localeCompare(b.quadrant);
  return a.position - b.position;
}
